import React, { useState, useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import { initiateRegistrationApi, verifyRegistrationOtpApi } from '../services/api';
import { fetchLookupDataApi } from '../services/lookupApi';
import RegisterStepForm from '../components/auth/RegisterStepForm';
import RegisterOtpVerifyForm from '../components/auth/RegisterOtpVerifyForm';
import TermsAndConditionsModal from '../components/auth/TermsAndConditionsModal';

const ROLES = {
    TALENT: { name: 'Talent', desc: '', code: '300' },
    PRODUCER: { name: 'Director/Producer', desc: '', code: '200' },
    CASTING: { name: 'Casting Director', desc: '', code: '400' },
};

const RegisterPage = () => {
    // Form States
    const [step, setStep] = useState(1);
    const [fullName, setFullName] = useState('');
    const [mobile, setMobile] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState(''); 
    const [showPassword, setShowPassword] = useState(false);
    const [tnc, setTnc] = useState(false);
    const [selectedRoleCode, setSelectedRoleCode] = useState(ROLES.TALENT.code);
    const [otp, setOtp] = useState('');
    
    const [selectedTalent, setSelectedTalent] = useState(null);
    
    // UI & API States
    const [userId, setUserId] = useState(null); 
    const [showTalentList, setShowTalentList] = useState(true); 
    const [lookupOptions, setLookupOptions] = useState({ talent: [] });
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    
    // Terms & Conditions Modal State
    const [showTncModal, setShowTncModal] = useState(false);
    const [isTncRead, setIsTncRead] = useState(false);
    
    const navigate = useNavigate();

    useEffect(() => {
        const loadTalent = async () => {
            try {
                const res = await fetchLookupDataApi('jobTalent');
                const options = res.data.data.map(item => ({
                    value: item.lookupValue,
                    label: item.lookupLabel
                }));
                setLookupOptions({ talent: options });
            } catch (err) {
                console.error("API failed, using static");
                setLookupOptions({
                    talent: [
                        { value: 'actor', label: 'Actor' },
                        { value: 'comedian', label: 'Comedian' },
                        { value: 'castingDirector', label: 'Casting Director' },
                        { value: 'model', label: 'Model' },
                        { value: 'scriptWriter', label: 'Script Writer' },
                        { value: 'filmAndStageCrew', label: 'Film And Stage Crew' },
                        { value: 'dancer', label: 'Dancer' },
                        { value: 'musician', label: 'Musician' },
                        { value: 'singer', label: 'Singer' },
                        { value: 'anchorEmcee', label: 'Anchor / Emcee' },
                        { value: 'sportsCommentator', label: 'Sports Commentator' },
                        { value: 'assistantDirector', label: 'Assistant Director' },
                        { value: 'director', label: 'Director' },
                        { value: 'producer', label: 'Producer' },
                        { value: 'choreographer', label: 'Choreographer' },
                        { value: 'cinematographer', label: 'Cinematographer' },
                        { value: 'editor', label: 'Editor' },
                        { value: 'soundEngineer', label: 'Sound Engineer' },
                        { value: 'lightingTechnician', label: 'Lighting Technician' },
                        { value: 'costumeDesigner', label: 'Costume Designer' }
                    ]
                });
            }
        };
        loadTalent();
    }, []);

    const validateForm = () => {
        const newErrors = {};
        if (!fullName.trim()) newErrors.fullName = "Full name is required.";
        
        const mobileRegex = /^[0-9]{10,15}$/;
        if (!mobileRegex.test(mobile)) newErrors.mobile = "Enter a valid mobile number (10-15 digits).";
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) newErrors.email = "Enter a valid email address.";
        
        if (password.length < 6) newErrors.password = "Password must be at least 6 characters.";
        
        if (selectedRoleCode === ROLES.TALENT.code && !selectedTalent) {
            newErrors.talent = "Please select your primary talent.";
        }

        if (!tnc) newErrors.tnc = "You must accept the terms and conditions.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInitiateRegistration = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        
        setLoading(true);
        const job_talent = selectedTalent ? [selectedTalent.value] : [];
        
        try {
            const { data } = await initiateRegistrationApi({ 
                fullName, email, mobileNo: mobile, roleCode: selectedRoleCode, password, talent: job_talent 
            });

            if (data.userId) {
                setUserId(data.userId); 
                setMessage(data.message || 'OTP sent to your email.');
                setIsError(false);
                setStep(2);
            }
        } catch (err) {
            setMessage(err.response?.data?.message || 'Registration failed.');
            setIsError(true);
        }
        setLoading(false);
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (!userId) { setMessage('Invalid session. Please try again.'); setIsError(true); return; }
        if (otp.length < 6) { setMessage('Enter a valid 6-digit OTP.'); setIsError(true); return; }

        setLoading(true);
        try {
            await verifyRegistrationOtpApi({ userId: userId, otp });
            setMessage('Account verified! Redirecting to login...');
            setIsError(false);
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            setMessage(err.response?.data?.message || 'OTP verification failed.');
            setIsError(true);
        }
        setLoading(false);
    };

    const openTncModal = (e) => {
        e.preventDefault();
        setIsTncRead(false);
        setShowTncModal(true);
    };

    const handleAcceptTerms = () => {
        setTnc(true);
        setShowTncModal(false);
        setErrors(prev => ({ ...prev, tnc: null })); 
    };

    const handleTncScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 5) {
            setIsTncRead(true);
        }
    };

    return (
         <AuthLayout 
            pageTitle="Register" 
            sidebarTitle="Create Your Account"
            subtitle="Join our community and start your journey today"
            leftTitle={<>Join India's Most Trusted Platform <br /><span className="text-red">for Talent</span></>}
            leftSubtitle="Create your account and unlock a world of auditions, castings, workshops and more."
        >
            {message && <Alert variant={isError ? "danger" : "success"} className="py-2 small mb-3">{message}</Alert>}
            <div className='registerPage'>
                {step === 1 ? (
                    <RegisterStepForm
                        selectedRoleCode={selectedRoleCode}
                        setSelectedRoleCode={setSelectedRoleCode}
                        showTalentList={showTalentList}
                        setShowTalentList={setShowTalentList}
                        setSelectedTalent={setSelectedTalent}
                        selectedTalent={selectedTalent}
                        lookupOptions={lookupOptions}
                        fullName={fullName}
                        setFullName={setFullName}
                        mobile={mobile}
                        setMobile={setMobile}
                        email={email}
                        setEmail={setEmail}
                        password={password}
                        setPassword={setPassword}
                        setConfirmPassword={setConfirmPassword}
                        showPassword={showPassword}
                        setShowPassword={setShowPassword}
                        tnc={tnc}
                        setTnc={setTnc}
                        openTncModal={openTncModal}
                        errors={errors}
                        setErrors={setErrors}
                        handleInitiateRegistration={handleInitiateRegistration}
                        loading={loading}
                    />
                ) : (
                    <RegisterOtpVerifyForm
                        email={email}
                        otp={otp}
                        setOtp={setOtp}
                        handleVerifyOtp={handleVerifyOtp}
                        setStep={setStep}
                        loading={loading}
                    />
                )}
            </div>

            <TermsAndConditionsModal
                showTncModal={showTncModal}
                setShowTncModal={setShowTncModal}
                handleTncScroll={handleTncScroll}
                isTncRead={isTncRead}
                handleAcceptTerms={handleAcceptTerms}
            />
        </AuthLayout>
    );
};

export default RegisterPage;